import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, ChevronDown, ChevronUp, CornerDownLeft } from 'lucide-react';
import './GlobalSearch.css';

// Full-page global search results (/search?q=<term>[&type=<group>]) — the
// "View all" target of the GlobalSearch dropdown. Same /api/global-search
// backend and the same row shape as the dropdown (tag, number, title, status,
// amount, date), rendered as a full-width table.
//
// With a &type= the page shows one group paginated ("Load more" appends pages
// of 50 via the offset param). Without a type it shows every group's first 10
// rows stacked, each with its own "View all" button. Rows behave like
// dropdown rows: navigate to url when present, else expand the info-card
// fields inline.

const PAGE_SIZE = 50;

const GROUP_OPTIONS = [
  { type: null, label: 'All' },
  { type: 'salesOrder', label: 'Sales Orders' },
  { type: 'invoice', label: 'Invoices' },
  { type: 'fulfillment', label: 'Fulfillments' },
  { type: 'payment', label: 'Customer Payments' },
  { type: 'purchaseOrder', label: 'Purchase Orders' },
  { type: 'workOrder', label: 'Work Orders' },
  { type: 'customer', label: 'Customers' },
  { type: 'vendor', label: 'Vendors' },
  { type: 'item', label: 'Items' },
];

const TYPE_TAGS = {
  salesOrder: 'SO',
  invoice: 'INV',
  fulfillment: 'IF',
  payment: 'PMT',
  purchaseOrder: 'PO',
  workOrder: 'WO',
  customer: 'CUST',
  vendor: 'VEN',
  item: 'ITEM',
};

const fmtMoney = (n) =>
  n == null ? null : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Append a page of rows to a group, deduping by row id (exact-first ranking
// on page 0 can resurface a row in plain fuzzy order on a later page).
function appendRows(prev, next) {
  const seen = new Set(prev.map((r) => r.id));
  return [...prev, ...next.filter((r) => !seen.has(r.id))];
}

export default function GlobalSearchResults() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();
  const type = searchParams.get('type') || null;

  const [input, setInput] = useState(q);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  // Keep the input in sync when the URL changes from outside (dropdown link).
  useEffect(() => {
    setInput(q);
  }, [q]);

  // Debounced: typing in the page's search box updates the URL, which drives
  // the fetch below.
  const onInputChange = (value) => {
    setInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params = {};
      if (value.trim()) params.q = value.trim();
      if (type) params.type = type;
      setSearchParams(params, { replace: true });
    }, 300);
  };
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const setType = (nextType) => {
    const params = {};
    if (q) params.q = q;
    if (nextType) params.type = nextType;
    setSearchParams(params);
  };

  // Fetch page 0 whenever the query or type changes.
  useEffect(() => {
    if (q.length < 2) {
      setGroups([]);
      setNote(q.length > 0 ? 'Type at least 2 characters to search.' : null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const seq = ++requestSeq.current;
    setLoading(true);
    (async () => {
      try {
        const params = type ? { q, type, limit: PAGE_SIZE } : { q, limit: 10 };
        const { data } = await axios.get('/api/global-search', { params });
        if (cancelled || seq !== requestSeq.current) return;
        setGroups(data.groups || []);
        setNote(data.note || null);
        setError(null);
        setExpandedId(null);
      } catch (err) {
        if (cancelled || seq !== requestSeq.current) return;
        setError(err.response?.data?.error || 'Search failed');
        setGroups([]);
      } finally {
        if (!cancelled && seq === requestSeq.current) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q, type]);

  // "Load more" — typed mode only: next page in plain fuzzy order.
  const loadMore = useCallback(async () => {
    const g = groups[0];
    if (!type || !g || loadingMore) return;
    const offset = (g.offset ?? 0) + g.results.length;
    setLoadingMore(true);
    try {
      const { data } = await axios.get('/api/global-search', {
        params: { q, type, limit: PAGE_SIZE, offset },
      });
      const page = (data.groups || [])[0];
      setGroups((prev) => {
        const cur = prev[0];
        if (!cur || !page) return prev;
        return [{
          ...cur,
          offset: page.offset,
          hasMore: page.hasMore && page.results.length > 0,
          total: page.total > 0 ? page.total : cur.total,
          results: appendRows(cur.results, page.results),
        }];
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Loading more failed');
    } finally {
      setLoadingMore(false);
    }
  }, [groups, type, q, loadingMore]);

  const openRow = (row) => {
    if (row.url) {
      if (/^https?:\/\//i.test(row.url)) window.open(row.url, '_blank', 'noopener');
      else navigate(row.url);
    } else {
      setExpandedId((prev) => (prev === row.id ? null : row.id));
    }
  };

  const renderTable = (group) => (
    <table className="gsx-page-table">
      <thead>
        <tr>
          <th className="gsx-col-tag">Type</th>
          <th className="gsx-col-number">Number</th>
          <th>Name</th>
          <th className="gsx-col-status">Status</th>
          <th className="gsx-col-amount">Amount</th>
          <th className="gsx-col-date">Date</th>
          <th className="gsx-col-action" aria-label="Open" />
        </tr>
      </thead>
      <tbody>
        {group.results.map((row) => {
          const isExpanded = expandedId === row.id;
          return (
            <React.Fragment key={row.id}>
              <tr
                className={`gsx-page-row ${isExpanded ? 'expanded' : ''}`}
                onClick={() => openRow(row)}
              >
                <td className="gsx-col-tag"><span className="gsx-row-tag">{TYPE_TAGS[row.recordType] || ''}</span></td>
                <td className="gsx-col-number">{row.number ? <code className="gsx-row-number">{row.number}</code> : '—'}</td>
                <td className="gsx-page-title">{row.title || '—'}</td>
                <td className="gsx-col-status">{row.status || ''}</td>
                <td className="gsx-col-amount">{row.amount != null ? fmtMoney(row.amount) : ''}</td>
                <td className="gsx-col-date">{row.date || ''}</td>
                <td className="gsx-col-action">
                  <span className="gsx-row-action">
                    {row.url
                      ? <CornerDownLeft size={13} />
                      : isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </span>
                </td>
              </tr>
              {isExpanded && !row.url && (
                <tr className="gsx-page-cardrow">
                  <td colSpan={7}>
                    <div className="gsx-card">
                      <dl className="gsx-card-grid">
                        {(row.fields || []).map((f) => (
                          <React.Fragment key={f.label}>
                            <dt>{f.label}</dt>
                            <dd>{typeof f.value === 'number' && /balance|remaining|overdue/i.test(f.label) ? fmtMoney(f.value) : String(f.value)}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                      <p className="gsx-card-note">No record page for this row type yet — key fields shown from the synced mirror.</p>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  const typedGroup = type ? groups[0] : null;
  const visibleGroups = groups.filter((g) => g.results.length > 0);

  return (
    <div className="gsx-page">
      <h1 className="gsx-page-heading">Search results</h1>

      <div className="gsx-page-searchrow">
        <div className="gsx-input-row open">
          <Search size={15} className="gsx-input-icon" />
          <input
            className="gsx-input"
            placeholder="Search orders, invoices, customers, items…"
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            aria-label="Search"
          />
          {(loading || loadingMore) && <span className="gsx-spinner" aria-hidden="true" />}
        </div>
      </div>

      <div className="gsx-page-pills" role="tablist" aria-label="Result type">
        {GROUP_OPTIONS.map((opt) => (
          <button
            key={opt.type || 'all'}
            type="button"
            className={`gsx-pill ${((opt.type || null) === type) ? 'active' : ''}`}
            onClick={() => setType(opt.type)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && <div className="gsx-message gsx-error">{error}</div>}
      {!error && q.length < 2 && (
        <div className="gsx-message">{note || 'Type at least 2 characters to search.'}</div>
      )}
      {!error && q.length >= 2 && !loading && visibleGroups.length === 0 && (
        <div className="gsx-message">{note || `No matches for "${q}".`}</div>
      )}

      {!error && type && typedGroup && typedGroup.results.length > 0 && (
        <div className="gsx-page-section">
          <div className="gsx-page-section-header">
            <span>{typedGroup.label}</span>
            <span className="gsx-group-count">
              {typedGroup.results.length < typedGroup.total
                ? `${typedGroup.results.length} of ${typedGroup.total.toLocaleString()}`
                : typedGroup.total.toLocaleString()}
            </span>
          </div>
          {renderTable(typedGroup)}
          {typedGroup.hasMore && (
            <div className="gsx-page-more">
              <button type="button" className="gsx-page-more-btn" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      {!error && !type && visibleGroups.map((group) => (
        <div className="gsx-page-section" key={group.type}>
          <div className="gsx-page-section-header">
            <span>{group.label}</span>
            {group.total > group.results.length ? (
              <button type="button" className="gsx-group-viewall" onClick={() => setType(group.type)}>
                View all {group.total.toLocaleString()} &rarr;
              </button>
            ) : (
              <span className="gsx-group-count">{group.total.toLocaleString()}</span>
            )}
          </div>
          {renderTable(group)}
        </div>
      ))}
    </div>
  );
}
